import { useEffect, useState } from 'react'
import { AlertTriangle, Info, Trash2 } from 'lucide-react'
import { PageHeader } from '@/components/ui/PageHeader'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { DataTable, type DataTableColumn } from '@/components/ui/DataTable'
import { Pagination } from '@/components/ui/Pagination'
import { InfoPopover } from '@/components/ui/InfoPopover'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { type PageSize } from '@/hooks/useCrudList'
import { useDataTableExport } from '@/hooks/useDataTableExport'
import { useAuthority } from '@/hooks/useAuthority'
import { IntegrationErrorLogsService, type IntegrationErrorLog } from '@/services/modules/IntegrationErrorLogsService'
import { toast } from '@/components/ui/Toast'
import { extractApiError } from '@/lib/api-errors'
import type { PaginationMeta } from '@/types/api'

function fmtDateTime(iso: string | null): string {
  if (!iso) return '—'
  try { return new Date(iso).toLocaleString('pt-BR') } catch { return iso }
}

/**
 * Adapter para o export "Todos os registros": o endpoint limita `size` a 200
 * (e `size=0` cai no padrão de 25), então percorre todas as páginas.
 */
const errorLogsExportService = {
  async list(params?: Record<string, unknown>) {
    const all: IntegrationErrorLog[] = []
    let page = 1
    let lastPage: number
    do {
      const res = await IntegrationErrorLogsService.list({ ...(params ?? {}), page, size: 200 })
      all.push(...res.data)
      lastPage = res.meta?.last_page ?? 1
      page++
    } while (page <= lastPage)
    return { data: all, meta: { total: all.length } }
  },
}

export function IntegrationsErrorLogsPage() {
  const [rows, setRows] = useState<IntegrationErrorLog[]>([])
  const [meta, setMeta] = useState<PaginationMeta | null>(null)
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [size, setSize] = useState<PageSize>(25)
  const [reload, setReload] = useState(0)
  const [removeTarget, setRemoveTarget] = useState<IntegrationErrorLog | null>(null)
  const [clearOpen, setClearOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const { can } = useAuthority()
  const canDelete = can('integrations.error_logs.delete')

  useEffect(() => {
    let alive = true
    setLoading(true)
    IntegrationErrorLogsService.list({ page, size })
      .then((res) => { if (!alive) return; setRows(res.data); setMeta(res.meta) })
      .catch(() => { if (alive) toast.error('Erro ao carregar os logs.') })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [page, size, reload])

  const exp = useDataTableExport({
    filename: 'logs-de-erro',
    service: errorLogsExportService,
    columns: [
      { header: 'Data/hora',  accessor: (l: IntegrationErrorLog) => (l.createdAt ? fmtDateTime(l.createdAt) : '') },
      { header: 'Integração', accessor: (l: IntegrationErrorLog) => l.integration },
      { header: 'Contexto',   accessor: (l: IntegrationErrorLog) => l.context },
      { header: 'Erro',       accessor: (l: IntegrationErrorLog) => l.message },
      { header: 'Referência', accessor: (l: IntegrationErrorLog) => (l.referenceType ? `${l.referenceType} ${l.referenceId ?? ''}`.trim() : '') },
      { header: 'Payload',    accessor: (l: IntegrationErrorLog) => (l.payload ? JSON.stringify(l.payload) : '') },
    ],
  })

  async function confirmRemove() {
    if (!removeTarget) return
    setBusy(true)
    try {
      await IntegrationErrorLogsService.remove(removeTarget.id)
      toast.success({ title: 'Log excluído', description: 'O registro foi removido.' })
      setRemoveTarget(null); setReload((v) => v + 1)
    } catch (e) {
      toast.error({ title: 'Não foi possível excluir', description: extractApiError(e).message })
    } finally { setBusy(false) }
  }

  async function confirmClear() {
    setBusy(true)
    try {
      const r = await IntegrationErrorLogsService.clearAll()
      toast.success({ title: 'Logs limpos', description: `${r.deleted} registro(s) removido(s).` })
      setClearOpen(false); setPage(1); setReload((v) => v + 1)
    } catch (e) {
      toast.error({ title: 'Não foi possível limpar', description: extractApiError(e).message })
    } finally { setBusy(false) }
  }

  const columns: DataTableColumn<IntegrationErrorLog>[] = [
    { key: 'createdAt', header: 'Data/hora', align: 'center', className: 'w-44 whitespace-nowrap',
      render: (l) => <span className="text-xs text-gray-600">{fmtDateTime(l.createdAt)}</span> },
    { key: 'integration', header: 'Integração', align: 'center', className: 'w-28',
      render: (l) => <Badge variant="gray">{l.integration}</Badge> },
    { key: 'context', header: 'Contexto', align: 'center', className: 'w-44',
      render: (l) => <span className="text-xs text-gray-700">{l.context}</span> },
    { key: 'message', header: 'Erro', alwaysVisible: true, className: 'w-full',
      render: (l) => (
        <div className="flex items-center gap-2">
          <AlertTriangle size={14} className="shrink-0 text-rose-500" />
          <span className="text-sm text-gray-800">{l.message}</span>
          {l.payload && (
            <InfoPopover align="left" ariaLabel="Detalhes" trigger={<Info size={14} className="text-gray-400 hover:text-brand-500" />} title="Payload">
              <pre className="max-w-xs whitespace-pre-wrap break-words text-[11px] text-gray-700">{JSON.stringify(l.payload, null, 2)}</pre>
              {l.referenceType && (
                <div className="mt-2 text-[11px] text-gray-500">Ref.: {l.referenceType} {l.referenceId}</div>
              )}
            </InfoPopover>
          )}
        </div>
      ) },
  ]

  return (
    <>
      <PageHeader
        title="Logs de erro"
        subtitle="Falhas de integração (ex.: cobrança que não pôde ser criada no Asaas)"
        crumbs={[{ label: 'Integrações' }, { label: 'Logs de erro' }]}
        actions={canDelete && rows.length > 0 ? (
          <Button variant="danger" iconLeft={<Trash2 size={14} />} onClick={() => setClearOpen(true)}>Excluir tudo</Button>
        ) : undefined}
      />

      <DataTable
        tableId="integrations.error-logs"
        exportable
        onExportPage={() => exp.exportPage(rows)}
        onExportAll={exp.exportAll}
        columns={columns}
        rows={rows}
        loading={loading}
        getRowKey={(l) => l.id}
        emptyMessage="Nenhum erro registrado."
        rowActions={canDelete ? (l) => [
          { key: 'remove', label: 'Excluir', icon: <Trash2 size={14} />, danger: true, onClick: () => setRemoveTarget(l) },
        ] : undefined}
      />
      <Pagination meta={meta} onChange={setPage} size={size} onSizeChange={setSize} />

      <ConfirmDialog
        open={!!removeTarget}
        onClose={() => setRemoveTarget(null)}
        onConfirm={confirmRemove}
        loading={busy}
        title="Excluir este log?"
        description="O registro será removido permanentemente."
        confirmLabel="Excluir"
      />
      <ConfirmDialog
        open={clearOpen}
        onClose={() => setClearOpen(false)}
        onConfirm={confirmClear}
        loading={busy}
        title="Excluir tudo os logs?"
        description="Todos os registros de erro serão removidos permanentemente."
        confirmLabel="Excluir tudo"
      />
    </>
  )
}

export default IntegrationsErrorLogsPage
